import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { BrandLogo } from '../shared/components/BrandLogo';
import { Icon } from '../shared/components/Icon';
import { useAuth } from '../features/auth/AuthContext';
import { readPreference, writePreference } from '../shared/lib/preferences';
import { useBodyScrollLock, useEscapeKey, useIsMobileShell } from '../shared/lib/responsive';
import { ContextBar } from './ContextBar';
import { SidebarNavigation } from './SidebarNavigation';
import { AssistantPanel } from '../features/assistant/AssistantPanel';
import { OfflineNotice } from '../offline/OfflineNotice';
import { SyncCentre } from '../offline/SyncCentre';
import { useDocumentLink } from '../workspace/documentLink';
import { allNavigation, mobileTabs } from '../workspace/navigation';
import { WorkspaceCanvas } from '../workspace/WorkspaceCanvas';
import { WorkspaceProvider, useWorkspaceActions } from '../workspace/WorkspaceProvider';
import { WorkspaceStatusBar } from '../workspace/WorkspaceStatusBar';
import { VoiceCommandProvider, useVoiceCommand } from '../ai/local/context/VoiceCommandProvider';
import { VoiceCommandLauncher } from '../ai/local/components/VoiceCommandLauncher';

const assistantWidthKey = 'varza.assistant.width';
const defaultAssistantWidth = 400;
const minimumAssistantWidth = 320;

/**
 * The docked assistant never takes more than half the window, however wide it was left on a
 * larger screen. The stylesheet clamps the same way, so a window resized under the stored
 * width narrows the column without waiting for anyone to drag it back.
 */
function clampAssistantWidth(width: number): number {
  const room = Math.max(window.innerWidth, minimumAssistantWidth * 2);
  return Math.round(Math.min(Math.max(width, minimumAssistantWidth), Math.min(720, room / 2)));
}

export function AppShell() {
  const location = useLocation();
  // The workspace is seeded with whatever address the console was entered on, so a
  // bookmarked report still opens as the first document rather than the dashboard.
  const [entryAddress] = useState(() => `${location.pathname}${location.search}`);

  return (
    <WorkspaceProvider initialAddress={entryAddress}>
      <VoiceCommandProvider>
        <ConsoleShell />
      </VoiceCommandProvider>
    </WorkspaceProvider>
  );
}

function ConsoleShell() {
  const { t } = useTranslation();
  const location = useLocation();
  const openDocument = useDocumentLink();
  const { open } = useWorkspaceActions();
  const { runText, manifest: voiceManifest } = useVoiceCommand();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantWidth, setAssistantWidth] = useState(() => {
    const stored = Number.parseInt(readPreference(assistantWidthKey), 10);
    return Number.isFinite(stored) ? clampAssistantWidth(stored) : defaultAssistantWidth;
  });
  const [commandQuery, setCommandQuery] = useState('');
  const { isOfflineSession, logout, session, userLabel } = useAuth();
  const isMobile = useIsMobileShell();

  const closeDrawer = useCallback(() => setMobileOpen(false), []);
  const resizeAssistant = useCallback((width: number) => {
    const next = clampAssistantWidth(width);
    setAssistantWidth(next);
    writePreference(assistantWidthKey, String(next));
  }, []);

  /*
   * On a phone the assistant is still a sheet over the page. On a desktop it is docked: a
   * column of the shell beside the workspace, which stays live and scrollable behind — so
   * neither the scroll lock nor a shell-wide Escape applies to it. The panel keeps its own
   * Escape, scoped to the focus inside it.
   */
  const assistantDocked = assistantOpen && !isMobile;

  // A drawer and a command palette both take the whole screen on a phone, so the page
  // behind them stays put rather than scrolling away under the reader's finger.
  useBodyScrollLock(mobileOpen || commandOpen || (assistantOpen && isMobile));
  useEscapeKey(mobileOpen, closeDrawer);

  const avatarLabel = useMemo(() => {
    const identityName = userLabel.includes('@') ? userLabel.slice(0, userLabel.indexOf('@')) : userLabel;
    const parts = identityName.trim().split(/\s+/u).filter(Boolean);
    return parts.slice(0, 2).map((part) => part[0]).join('').toLocaleUpperCase() || 'U';
  }, [userLabel]);

  const authenticationLabel = session?.authenticationStrength.toLocaleLowerCase() === 'mfa'
    ? t('auth.mfaSession')
    : t('auth.passwordSession');

  useEffect(() => {
    setMobileOpen(false);
    setCommandOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleKeyboardShortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLocaleLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen(true);
      }
      if (commandOpen && event.key === 'Escape') setCommandOpen(false);
    };
    document.addEventListener('keydown', handleKeyboardShortcut);
    return () => document.removeEventListener('keydown', handleKeyboardShortcut);
  }, [commandOpen]);

  const commandItems = useMemo(
    () => allNavigation.filter((item) => t(item.labelKey).toLocaleLowerCase().includes(commandQuery.toLocaleLowerCase())),
    [commandQuery, t],
  );

  const accountMenu = (
    <div className="popover__panel popover__panel--end account-menu">
      <div className="account-menu__identity">
        <strong>{userLabel}</strong>
        <small>{authenticationLabel}</small>
      </div>
      <Link to="/console/settings" onClick={openDocument('/console/settings')}><Icon name="settings" />{t('nav.settings')}</Link>
      {session?.canCreateInvitations && <Link to="/invitations"><Icon name="send" />{t('invitations.title')}</Link>}
      <button type="button" onClick={() => void logout()}>
        <Icon name="arrow" className="icon directional-icon" />{t('auth.logout')}
      </button>
    </div>
  );

  return (
    <div
      className={`app-shell app-shell--workspace${collapsed ? ' app-shell--collapsed' : ''}${assistantDocked ? ' app-shell--assistant' : ''}`}
      style={{ '--assistant-width': `${assistantWidth}px` } as CSSProperties}
    >
      <a className="skip-link" href="#main-content">{t('app.skipToContent')}</a>

      {mobileOpen && <button type="button" className="mobile-scrim" aria-label={t('nav.closeMenu')} onClick={closeDrawer} />}

      <aside className={`sidebar${mobileOpen ? ' sidebar--mobile-open' : ''}`} aria-label={t('app.workspace')}>
        <div className="brand">
          {/* The mark is the way back out of the console, as it is on any site. */}
          <Link className="brand__home" to="/" aria-label={t('nav.homepage')}>
            <span className="brand__mark" aria-hidden="true"><BrandLogo /></span>
            {!collapsed && <span className="brand__copy"><strong>{t('app.name')}</strong><small>{t('app.tagline')}</small></span>}
          </Link>
          <button className="icon-button sidebar__mobile-close" type="button" onClick={closeDrawer} aria-label={t('nav.closeMenu')}>
            <Icon name="close" />
          </button>
        </div>

        {/* The width control sits above the links it governs, and is icon only. */}
        <div className="sidebar__tools">
          <button
            className="icon-button collapse-button"
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
            title={collapsed ? t('nav.expand') : t('nav.collapse')}
          >
            <Icon name="chevron" className="icon directional-icon" />
          </button>
        </div>

        <SidebarNavigation collapsed={collapsed} onNavigate={closeDrawer} onExpandRail={() => setCollapsed(false)} />

        <div className="sidebar__footer">
          <NavLink to="/console/settings/audit" onClick={openDocument('/console/settings/audit', closeDrawer)} className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`} title={collapsed ? t('auditLog.title') : undefined}>
            <Icon name="reports" /><span className={collapsed ? 'sr-only' : ''}>{t('auditLog.title')}</span>
          </NavLink>
          <NavLink to="/console/settings/access" onClick={openDocument('/console/settings/access', closeDrawer)} className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`} title={collapsed ? t('nav.access') : undefined}>
            <Icon name="user" /><span className={collapsed ? 'sr-only' : ''}>{t('nav.access')}</span>
          </NavLink>

          {/*
            The top bar drops the account summary on a phone for want of room, so the
            drawer carries the identity, its settings, and the way out instead.
          */}
          {isMobile && (
            <div className="sidebar__account">
              <div className="sidebar__account-identity">
                <span className="avatar" aria-hidden="true">{avatarLabel}</span>
                <span><strong>{userLabel}</strong><small>{authenticationLabel}</small></span>
              </div>
              <NavLink to="/console/settings" onClick={openDocument('/console/settings', closeDrawer)} className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`}>
                <Icon name="settings" /><span>{t('nav.settings')}</span>
              </NavLink>
              {session?.canCreateInvitations && (
                <NavLink to="/invitations" onClick={closeDrawer} className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`}>
                  <Icon name="send" /><span>{t('invitations.title')}</span>
                </NavLink>
              )}
              <button className="nav-link" type="button" onClick={() => void logout()}>
                <Icon name="arrow" className="icon directional-icon" /><span>{t('auth.logout')}</span>
              </button>
            </div>
          )}
        </div>
      </aside>

      <div className="app-frame">
        <header className="topbar">
          <div className="topbar__primary">
            <button className="icon-button mobile-menu-button" type="button" onClick={() => setMobileOpen(true)} aria-label={t('nav.openMenu')}>
              <Icon name="menu" />
            </button>

            {/* On a phone the scope moves up beside the search so the header stays one row tall. */}
            {isMobile && <ContextBar compact />}

            <button className="command-trigger" type="button" onClick={() => setCommandOpen(true)} aria-label={t('common.search')}>
              <Icon name="search" />
              <span>{t('common.search')}</span>
              <kbd>Ctrl K</kbd>
            </button>
            <div className="topbar__actions">
              {/* Speaks up only when the connection or the queue needs saying something. */}
              <SyncCentre />
              {/* Language and theme are preferences, so they live in Settings. */}
              <details className="popover">
                <summary className="icon-button notification-button" aria-label={t('context.notifications')}>
                  <Icon name="bell" /><span className="notification-dot" />
                </summary>
                <div className="popover__panel popover__panel--end">
                  <strong>{t('context.pendingApprovals', { count: 3 })}</strong>
                  <p>{t('dashboard.attentionSubtitle')}</p>
                </div>
              </details>
              {!isMobile && (
                <details className="popover">
                  <summary className="user-summary" aria-label={t('context.userMenu')}>
                    <span className="avatar" aria-hidden="true">{avatarLabel}</span>
                    <span className="user-summary__copy"><strong>{userLabel}</strong><small>{authenticationLabel}</small></span>
                    <Icon name="chevron" className="icon icon--small directional-icon--down" />
                  </summary>
                  {accountMenu}
                </details>
              )}
            </div>
          </div>

          {!isMobile && <ContextBar />}
        </header>

        {/* Between the chrome and the page, because it qualifies everything below it. */}
        <OfflineNotice sessionIsOffline={isOfflineSession} />

        {/*
          One canvas, many documents. The rail and the palette below no longer replace what
          is on screen: they add a tab to the workspace, and the workspace decides where it
          goes — beside another document, over it, or in a window of its own.
        */}
        <main id="main-content" className="main-content" tabIndex={-1}>
          <WorkspaceCanvas />
        </main>

        <footer className="app-footer app-footer--workspace">
          <WorkspaceStatusBar />
        </footer>
      </div>

      {/* The thumb rail: only on the phone shell, and never a duplicate tab stop on desktop. */}
      {isMobile && (
        <nav className="mobile-tabbar" aria-label={t('nav.quickNav')}>
          {/*
            Only the dashboard matches exactly; the rest stay lit for their own sub-routes,
            so opening a journal keeps the Journals tab current. `NavLink` marks
            `aria-current` itself from the same rule.
          */}
          {mobileTabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.to === '/console'}
              onClick={openDocument(tab.to)}
              className={({ isActive }) => `mobile-tabbar__item${isActive ? ' mobile-tabbar__item--active' : ''}`}
            >
              <Icon name={tab.icon} />
              <span>{t(tab.labelKey)}</span>
            </NavLink>
          ))}
          <button
            className={`mobile-tabbar__item${mobileOpen ? ' mobile-tabbar__item--active' : ''}`}
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-expanded={mobileOpen}
          >
            <Icon name="more" />
            <span>{t('common.more')}</span>
          </button>
        </nav>
      )}

      <button
        className={`assistant-launcher${assistantOpen ? ' assistant-launcher--active' : ''}`}
        type="button"
        onClick={() => setAssistantOpen((value) => !value)}
        aria-expanded={assistantOpen}
        aria-controls="varza-assistant-panel"
      >
        <span className="assistant-launcher__mark" aria-hidden="true" />
        <span>{t('assistant.askVarza')}</span>
      </button>

      <AssistantPanel
        open={assistantOpen}
        onClose={() => setAssistantOpen(false)}
        docked={assistantDocked}
        width={assistantWidth}
        onResize={resizeAssistant}
      />

      {commandOpen && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={() => setCommandOpen(false)}>
          <section className="command-dialog" role="dialog" aria-modal="true" aria-label={t('common.search')} onMouseDown={(event) => event.stopPropagation()}>
            <div className="command-dialog__search">
              <Icon name="search" />
              <input autoFocus value={commandQuery} onChange={(event) => setCommandQuery(event.target.value)} placeholder={t('common.search')} />
              <button className="icon-button" type="button" onClick={() => setCommandOpen(false)} aria-label={t('common.close')}><Icon name="close" /></button>
            </div>
            <div className="command-dialog__results">
              {commandQuery.trim() && voiceManifest?.features.enabled && (
                <button
                  className="command-dialog__voice"
                  type="button"
                  onClick={() => {
                    void runText(commandQuery).then((matched) => {
                      if (matched) setCommandOpen(false);
                    });
                  }}
                >
                  <Icon name="microphone" /><span>{t('voice.runTypedCommand', { command: commandQuery })}</span><Icon name="arrow" className="icon directional-icon" />
                </button>
              )}
              {commandItems.map((item) => (
                <button
                  key={item.to}
                  type="button"
                  onClick={() => {
                    setCommandOpen(false);
                    open(item.to);
                  }}
                >
                  <Icon name={item.icon} /><span>{t(item.labelKey)}</span><Icon name="arrow" className="icon directional-icon" />
                </button>
              ))}
              {commandItems.length === 0 && !commandQuery.trim() && <p>{t('common.noResults')}</p>}
            </div>
          </section>
        </div>
      )}

      <VoiceCommandLauncher />
    </div>
  );
}
